import api from './api';

export interface EstimateHeader {
  id: number;
  estimate_number?: string; // Numer kosztorysu w formacie EST.[nr].YYYY
  /** UUID oferty standalone (offers_standalone) — EasyEstimate */
  standalone_offer_id?: string | null;
  /** Utworzony z szablonu arkusz Google (sync przedmiaru EasyEstimate) */
  easy_przedmiar_google_sheet_id?: string | null;
  easy_przedmiar_google_sheet_url?: string | null;
  name: string;
  object_name: string;
  estimate_location?: string | null;
  /** Branża (max 100 znaków w bazie) */
  branza?: string | null;
  client: string;
  client_address?: string | null;
  contractor_name?: string | null;
  contractor_address?: string | null;
  markup_kp: number;
  markup_kz: number;
  markup_z: number;
  vat: number;
  rg?: number; // stawka roboczogodziny (opcjonalnie z ATH)
  preparer: string;
  reviewer?: string | null;
  prepared_at: string;
  updated_at: string;
  status: 'draft'|'finished'|'deleted';
  currency?: string;
  precision_unit_price?: number;
  precision_rms_price?: number;
  precision_quantity?: number;
  precision_expenses?: number;
  precision_norm?: number;
}

export interface EstimatePositionApi {
  id: number;
  estimate_id: number;
  nr: string;
  attachment: string;
  position_type: 'S' | 'U';
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  value: number;
  seq_no: number;
  rms: RmsApi[];
}

export interface RmsApi {
  id: number;
  rms_type: 'R' | 'M' | 'S' | 'Kp' | 'Kz' | 'Z';
  description: string;
  unit: string;
  norm: number;
  quantity: number;
  rms_price: number;
}

export interface EstimateFull extends EstimateHeader {
  positions: EstimatePositionApi[];
}

export const updateEstimateStatus = async (id:number, status:'draft'|'finished'|'deleted') => {
  await api.patch(`/estimates/${id}/status`, { status });
};

export const getEstimate = async (id:number): Promise<EstimateFull> => {
  const res = await api.get<EstimateFull>(`/estimates/${id}`);
  return res.data;
};

export const addPosition = async (estimateId:number, payload:any) => {
  const res = await api.post(`/estimates/${estimateId}/positions`, payload);
  return res.data;
};

export const getEstimateList = async (opts?: { standaloneOfferId?: string }): Promise<EstimateHeader[]> => {
  const res = await api.get<EstimateHeader[]>('/estimates', {
    params: opts?.standaloneOfferId ? { standalone_offer_id: opts.standaloneOfferId } : undefined,
  });
  return res.data;
};

/** Suma netto/VAT/brutto z kosztorysów przypisanych do oferty (UUID offers_standalone.id) — jak na stronie kosztorysów oferty. */
export type EasyEstimateOfferMoneyTotals = { net: number; vat: number; brutto: number };

export const getEasyEstimateTotalsByStandaloneOffer = async (): Promise<{
  totals: Record<string, EasyEstimateOfferMoneyTotals>;
}> => {
  const res = await api.get<{ totals: Record<string, EasyEstimateOfferMoneyTotals> }>(
    '/estimates/easy-totals-by-standalone-offer'
  );
  return res.data;
};

export interface CreateEstimatePayload {
  name: string;
  object_name: string;
  client: string;
  markup_kp: number;
  markup_kz: number;
  markup_z: number;
  vat: number;
  rg?: number;
  preparer: string;
  prepared_at: string; // YYYY-MM-DD
  /** UUID oferty standalone (EasyEstimate) */
  standalone_offer_id?: string;
  estimate_location?: string;
  branza?: string;
  client_address?: string;
  contractor_name?: string;
  contractor_address?: string;
  reviewer?: string;
}

export const createEstimate = async (payload: CreateEstimatePayload): Promise<EstimateFull> => {
  const res = await api.post<EstimateFull>('/estimates', payload);
  return res.data;
};

export const updateEstimate = async (id:number, payload:Partial<EstimateHeader>) => {
  const res = await api.patch<EstimateFull>(`/estimates/${id}`, payload);
  return res.data;
};

// TES
export interface AddTesSectionPayload {
  nr: string;
  rangeFrom: number;
  rangeTo: number;
  name: string;
}

export interface TesSectionApi {
  id: number;
  nr: string;
  range: string; // np. "1-20"
  name: string;
  total: number;
}

export const addTesSection = async (estimateId:number, payload:AddTesSectionPayload): Promise<TesSectionApi> => {
  const res = await api.post<TesSectionApi>(`/estimates/${estimateId}/tes`, payload);
  return res.data;
};

export const getTesSections = async (estimateId:number): Promise<TesSectionApi[]> => {
  const res = await api.get<TesSectionApi[]>(`/estimates/${estimateId}/tes`);
  return res.data;
};

export const updateTesSection = async (estimateId:number, tesId:number, payload:AddTesSectionPayload): Promise<TesSectionApi> => {
  const res = await api.patch<TesSectionApi>(`/estimates/${estimateId}/tes/${tesId}`, payload);
  return res.data;
};

export const deleteTesSection = async (estimateId:number, tesId:number) => {
  await api.delete(`/estimates/${estimateId}/tes/${tesId}`);
};

// RMS
export const updateRms = async (
  estimateId:number,
  rmsId:number,
  payload:Partial<{description:string; unit:string; rms_price:number; massUpdate?:boolean; old_price?:number;}>
) => {
  const res = await api.patch(`/estimates/${estimateId}/rms/${rmsId}`, payload);
  return res.data;
};

export const deleteRms = async (estimateId:number, rmsId:number) => {
  await api.delete(`/estimates/${estimateId}/rms/${rmsId}`);
};

export const updatePosition = async (estimateId:number, positionId:number, payload:Partial<{description:string; quantity:number; unit:string; unit_price:number}>)=>{
  const res = await api.patch(`/estimates/${estimateId}/positions/${positionId}`, payload);
  return res.data;
};

export const generatePrzedmiar = async (estimateId:number, files:File[]) => {
  const form = new FormData();
  files.forEach(f=> form.append('files', f));
  const res = await api.post(`/estimates/${estimateId}/przedmiar-ai`, form, {
    headers:{ 'Content-Type':'multipart/form-data' }
  });
  return res.data; // { positions: [...] }
}; 

// Import pliku Athenasoft (.ath) bezpośrednio do pozycji i RMS
export const importAth = async (estimateId:number, file:File) => {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post(`/estimates/${estimateId}/import-ath`, form, {
    headers:{ 'Content-Type':'multipart/form-data' }
  });
  return res.data as { imported_positions:number; imported_rms:number };
};

/** Wiersz przedmiaru EasyEstimate (GET / PUT easy-przedmiar). */
export interface EstimateEasyPrzedmiarRowDto {
  sortOrder: number;
  rowType: string;
  /** Alias z API / proxy (parsowanie toleruje oba warianty). */
  row_type?: string | null;
  dzialTitle?: string | null;
  dzialKey?: string | null;
  dzial_title?: string | null;
  dzial_key?: string | null;
  /** WBS rozdziału (1 / 1.1 / 1.2.1) — tylko wiersze rowType=dzial */
  dzialOutlineNr?: string | null;
  dzial_outline_nr?: string | null;
  lp?: string | null;
  podstawa?: string | null;
  opis?: string | null;
  jm?: string | null;
  ilosc?: string | null;
  uwagi?: string | null;
  pozycjaDzial?: string | null;
  pozycja_dzial?: string | null;
  extras?: Record<string, unknown> | null;
  /** Data zatwierdzenia pozycji (YYYY-MM-DD) — z kolumny SQL. */
  pozycjaApprovedAt?: string | null;
  pozycja_approved_at?: string | null;
  pozycjaApprovedByName?: string | null;
  pozycja_approved_by_name?: string | null;
  pozycjaApprovedByUserId?: string | null;
  pozycja_approved_by_user_id?: string | null;
}

export const getEstimateEasyPrzedmiar = async (
  estimateId: number
): Promise<{ success: boolean; rows: EstimateEasyPrzedmiarRowDto[]; message?: string }> => {
  const res = await api.get<{ success: boolean; rows?: EstimateEasyPrzedmiarRowDto[]; message?: string }>(
    `/estimates/${estimateId}/easy-przedmiar`
  );
  return {
    success: !!res.data.success,
    rows: Array.isArray(res.data.rows) ? res.data.rows : [],
    message: res.data.message,
  };
};

export const putEstimateEasyPrzedmiar = async (
  estimateId: number,
  rows: unknown[]
): Promise<{ success: boolean; saved?: number; message?: string }> => {
  const res = await api.put<{ success: boolean; saved?: number; message?: string }>(
    `/estimates/${estimateId}/easy-przedmiar`,
    { rows }
  );
  return res.data;
};

/** Snapshot przedmiaru EasyEstimate (wersjonowanie na serwerze — współdzielone między użytkownikami). */
export interface EasyPrzedmiarSnapshotPayload {
  tableEntries: unknown[];
  cellOverrides: Record<string, string>;
  calcScratchBundles: Record<string, unknown>;
}

export interface EasyPrzedmiarVersionFileDto {
  schemaVersion: number;
  estimateId: number;
  label: string;
  savedAt: string;
  origin?: 'manual' | 'auto_before_restore' | 'auto';
  estimateMeta?: { estimate_number?: string; name?: string; object_name?: string };
  payload: EasyPrzedmiarSnapshotPayload;
  id?: number;
}

export type EasyPrzedmiarVersionListItem = {
  id: number;
  label: string;
  savedAt: string;
  origin?: string | null;
};

export const listEasyPrzedmiarVersions = async (
  estimateId: number
): Promise<{ success: boolean; versions: EasyPrzedmiarVersionListItem[]; message?: string }> => {
  const res = await api.get<{
    success: boolean;
    versions?: EasyPrzedmiarVersionListItem[];
    message?: string;
  }>(`/estimates/${estimateId}/easy-przedmiar/versions`);
  return {
    success: !!res.data.success,
    versions: Array.isArray(res.data.versions) ? res.data.versions : [],
    message: res.data.message,
  };
};

export const createEasyPrzedmiarVersionSnapshot = async (
  estimateId: number,
  body: {
    payload: EasyPrzedmiarSnapshotPayload;
    origin?: 'manual' | 'auto_before_restore' | 'auto';
    estimateMeta?: { estimate_number?: string; name?: string; object_name?: string };
  }
): Promise<{ success: boolean; file?: EasyPrzedmiarVersionFileDto; message?: string }> => {
  const res = await api.post<{ success: boolean; file?: EasyPrzedmiarVersionFileDto; message?: string }>(
    `/estimates/${estimateId}/easy-przedmiar/versions`,
    body
  );
  return res.data;
};

export const getEasyPrzedmiarVersionSnapshot = async (
  estimateId: number,
  versionId: number
): Promise<{ success: boolean; file?: EasyPrzedmiarVersionFileDto; message?: string }> => {
  const res = await api.get<{ success: boolean; file?: EasyPrzedmiarVersionFileDto; message?: string }>(
    `/estimates/${estimateId}/easy-przedmiar/versions/${versionId}`
  );
  return res.data;
};

/** Utworzenie arkusza z szablonu (konto serwisowe) lub otwarcie istniejącego + synchronizacja danych. */
export const postEasyEstimatePrzedmiarGoogleSheet = async (
  estimateId: number
): Promise<{
  success: boolean;
  url?: string;
  spreadsheetId?: string;
  alreadyExists?: boolean;
  message?: string;
}> => {
  const res = await api.post<{
    success: boolean;
    url?: string;
    spreadsheetId?: string;
    alreadyExists?: boolean;
    message?: string;
  }>(`/estimates/${estimateId}/easy-przedmiar/google-sheet`);
  return res.data;
};